import * as React from 'react';

import {
  type GTMConfig,
  type SendToDataLayerParams,
  initGTM,
  sendToDataLayer,
  DEFAULT_DATA_LAYER_NAME,
  DEFAULT_DOMAIN,
  DEFAULT_SCRIPT_NAME,
} from './utils';

type State = GTMConfig & {
  dataLayerName: NonNullable<GTMConfig['dataLayerName']>;
  customDomain: NonNullable<GTMConfig['customDomain']>;
  customScriptName: NonNullable<GTMConfig['customScriptName']>;
};

export const initialState: State = {
  dataLayer: undefined,
  dataLayerName: DEFAULT_DATA_LAYER_NAME,
  environment: undefined,
  nonce: undefined,
  id: '',
  injectScript: true,
  customDomain: DEFAULT_DOMAIN,
  customScriptName: DEFAULT_SCRIPT_NAME,
};

export const GTMContext = React.createContext<State | undefined>(initialState);

export const GTMContextDispatch = React.createContext<
  ((data: SendToDataLayerParams['data']) => void) | undefined
>(undefined);

function reducer(state: State, action: Record<string, unknown>) {
  sendToDataLayer({
    dataLayerName: state.dataLayerName || DEFAULT_DATA_LAYER_NAME,
    data: action,
  });

  return state;
}

export type Props = {
  config?: GTMConfig;
  children: React.ReactNode;
};

export function GTMProvider({ config, children }: Props): React.JSX.Element {
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    ...config,
  });

  React.useEffect(() => {
    if (!config || config.injectScript === false) {
      return;
    }

    initGTM({ ...state, ...config });
  }, [config, state]);

  return (
    <GTMContext.Provider value={state}>
      <GTMContextDispatch.Provider value={dispatch}>{children}</GTMContextDispatch.Provider>
    </GTMContext.Provider>
  );
}

export function useGTMDispatch() {
  const context = React.useContext(GTMContextDispatch);

  if (context === undefined) {
    throw new Error('useGTMDispatch must be used within a GTMProvider');
  }

  return context;
}
